import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Aplica um filtro que veio na URL e some com o parâmetro.
 *
 * Existe por causa dos contadores do painel: "7 certificados a validar" tem de
 * levar aos sete, e não à lista inteira com o filtro em "Todas". Um contador
 * que promete um recorte e entrega outro ensina a não confiar no número.
 *
 * O parâmetro é **gatilho, não estado**: assim que o filtro é aplicado, ele sai
 * da URL. Mantê-lo criaria duas fontes da verdade — a pessoa troca o seletor, a
 * URL continua dizendo a outra coisa, e um F5 desfaz a escolha dela.
 *
 * `valores` é a lista do que o seletor aceita. Parâmetro fora dela é ignorado,
 * porque um link velho ou digitado à mão não pode colocar a tela num estado que
 * o seletor não sabe exibir.
 */
export function useUrlFilter<T extends string>(
  param: string,
  valores: readonly T[],
  aplicar: (valor: T) => void,
): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const bruto = searchParams.get(param);

  useEffect(() => {
    if (bruto === null) return;

    // Sai da URL mesmo quando o valor não serve: senão um parâmetro inválido
    // ficaria pendurado no endereço para sempre, sem efeito e sem explicação.
    const proximo = new URLSearchParams(searchParams);
    proximo.delete(param);
    setSearchParams(proximo, { replace: true });

    if ((valores as readonly string[]).includes(bruto)) aplicar(bruto as T);
    // `aplicar` é um setter de estado, estável entre renders; incluí-lo aqui
    // faria o efeito rodar de novo a cada digitação em qualquer outro filtro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bruto, param]);
}
